import request from './request'
import { AxiosResponse } from 'axios'
import {
  DriverApplication,
  DriverCarInformation,
  DriverApplicationDocumentType,
  DriverApplicationCarPhotoType,
  DriverApplicationForm,
  DriverApplicationDocument,
  DocValidity,
  DriverApplicationList,
  DriverApplicationDocumentItem,
  DriverApplicationCarPhotoItem,
  DriverApplicationReport,
  DriverApplicationAnalyticsOverview,
} from './types/driver-application'
import { DriverApplicationCarInfo } from './types/driver'

export const getDriverApplication = async (): Promise<AxiosResponse<DriverApplication>> => {
  const data = await request.get<DriverApplication>(`/driver-application/`)
  return data
}

export const getDriverApplicationCarInformation = async (
  applicationId: number,
): Promise<AxiosResponse<DriverCarInformation>> => {
  const data = await request.get<DriverCarInformation>(`/driver-application/${applicationId}/car-info`)
  return data
}

export const getDriverApplicationReport = async (
  applicationId: number,
): Promise<AxiosResponse<DriverApplicationReport>> => {
  const data = await request.get<DriverApplicationReport>(`/onfido/get-report/${applicationId}`)
  return data
}

export const getDriverApplicationDocument = async (
  applicationId: number,
  documentType: DriverApplicationDocumentType,
): Promise<AxiosResponse<DriverApplicationDocument>> => {
  const data = await request.get<DriverApplicationDocument>(`/driver-application/${applicationId}/docs/${documentType}`)
  return data
}

export const getDriverApplicationCarPhoto = async (
  applicationId: number,
  photoType: DriverApplicationCarPhotoType,
): Promise<AxiosResponse<DriverApplicationDocument>> => {
  const data = await request.get<DriverApplicationDocument>(
    `/driver-application/${applicationId}/car-photos/${photoType}`,
  )
  return data
}

// Backend requires first_name/last_name; use a placeholder when the user hasn't set a name yet.
// Admin UI hides the literal "default" label so these show as blank until Onfido fills real names.
const DEFAULT_DRIVER_APPLICATION_NAME = 'default'

export type StartDriverApplicationPayload = Partial<DriverApplicationForm> & {
  phone?: string
}

const hasContactInfo = (email?: string | null, mobile?: string | null) => {
  const normalizedEmail = email?.trim()
  const normalizedMobile = mobile?.trim()
  // Ignore placeholder phone values used in the form defaults
  const usableMobile = normalizedMobile && normalizedMobile !== '+' && normalizedMobile !== '+1'
  return Boolean(normalizedEmail || usableMobile)
}

export class DriverApplicationStartError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DriverApplicationStartError'
  }
}

const buildStartDriverApplicationPayload = (data: StartDriverApplicationPayload): Partial<DriverApplicationForm> => {
  const email = data.email?.trim()
  const mobile = (data.mobile || data.phone)?.trim()

  if (!hasContactInfo(email, mobile)) {
    throw new DriverApplicationStartError('Phone or email is required to start a driver application')
  }

  const payload: Partial<DriverApplicationForm> = {
    first_name: data.first_name?.trim() || DEFAULT_DRIVER_APPLICATION_NAME,
    last_name: data.last_name?.trim() || DEFAULT_DRIVER_APPLICATION_NAME,
  }

  if (email) payload.email = email
  if (mobile && mobile !== '+' && mobile !== '+1') payload.mobile = mobile

  return payload
}

export const startDriverApplication = async (data: StartDriverApplicationPayload): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>('/driver-application/', buildStartDriverApplicationPayload(data))
  return response
}

export const updateDriverApplication = async (data: DriverApplicationForm): Promise<AxiosResponse<any>> => {
  const response = await request.patch<any>('/driver-application/', data)
  return response
}

export const submitDriverApplication = async (applicationId: number): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/driver-application/${applicationId}/submit`)
  return response
}

export const saveCarInformation = async (
  applicationId: number,
  data: DriverCarInformation,
): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/driver-application/${applicationId}/car-info`, data)
  return response
}

export const updateCarInformation = async (
  applicationId: number,
  data: DriverCarInformation,
): Promise<AxiosResponse<any>> => {
  const response = await request.patch<any>(`/driver-application/${applicationId}/car-info`, data)
  return response
}

// export const uploadDriverDocument = async (
//   applicationId: number,
//   file: File,
//   additionalData: { [key: string]: string },
// ): Promise<AxiosResponse<any>> => {
//   const formData = new FormData()
//   formData.append('file', file)

//   // Append additional data
//   Object.keys(additionalData).forEach((key) => {
//     formData.append(key, additionalData[key])
//   })

//   const response = await request.post<any>(`/driver-application/${applicationId}/submit`, formData, {
//     headers: {
//       'Content-Type': 'multipart/form-data',
//     },
//   })
//   return response
// }

export const uploadDriverApplicationDocument = async (
  applicationId: number,
  documentType: DriverApplicationDocumentType,
  file: File,
): Promise<AxiosResponse<any>> => {
  const formData = new FormData()
  formData.append('document', file)

  const response = await request.post<any>(`/driver-application/${applicationId}/docs/${documentType}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response
}

export const setDocValidity = async (
  applicationId: number,
  documentType: DriverApplicationDocumentType,
  data: DocValidity,
): Promise<AxiosResponse<any>> => {
  const response = await request.patch<any>(`/driver-application/${applicationId}/docs/${documentType}`, data)
  return response
}

export const uploadDriverApplicationCarPhoto = async (
  applicationId: number,
  photoType: DriverApplicationCarPhotoType,
  file: File,
): Promise<AxiosResponse<any>> => {
  const formData = new FormData()
  formData.append('photo', file)

  const response = await request.post<any>(`/driver-application/${applicationId}/car-photos/${photoType}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response
}

export const sendVerificationCode = async (phone: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/phone-verification/send-code/`, { phone_number: phone })
  return response
}

export const verifyCode = async (phone: string, code: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/phone-verification/verify-code/`, { phone_number: phone, code })
  return response
}

export const sendEmailVerificationCode = async (email: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/email-verification/send-code/`, { email })
  return response
}

export const verifyEmailCode = async (email: string, code: string): Promise<AxiosResponse<any>> => {
  const response = await request.post<any>(`/email-verification/verify-code/`, { email, code })
  return response
}

// ADMIN SECTION

export interface DriverApplicationsSearchParams {
  offset?: number
  limit?: number
  is_ready_for_review?: boolean
  is_sent_for_review?: boolean
  ready_for_review?: boolean
  sent_for_review?: boolean
  is_reviewed?: boolean
  is_archived?: boolean
  is_rejected?: boolean
  for_review?: boolean
  in_progress?: boolean
  reviewed_by_id?: boolean
  q?: string
  order_by?: 'created_at' | 'updated_at'
  source?: string
}

export const listAllDriverApplications = async ({
  offset,
  limit,
  is_ready_for_review,
  is_sent_for_review,
  ready_for_review,
  sent_for_review,
  is_reviewed,
  is_archived,
  is_rejected,
  for_review,
  in_progress,
  q,
  order_by,
  source,
}: DriverApplicationsSearchParams = {}): Promise<AxiosResponse<DriverApplicationList>> => {
  const params = new URLSearchParams()

  if (offset !== undefined) params.append('offset', offset.toString())
  if (limit) params.append('limit', limit?.toString())
  if (is_ready_for_review) {
    params.append('is_ready_for_review', is_ready_for_review ? 'true' : 'false')
    params.append('approved', 'false')
    params.append('rejected', 'false')
  }
  if (is_sent_for_review) {
    params.append('is_sent_for_review', is_sent_for_review ? 'true' : 'false')
    params.append('approved', 'false')
    params.append('rejected', 'false')
  }
  if (ready_for_review !== undefined) {
    params.append('ready_for_review', ready_for_review ? 'true' : 'false')
  }
  if (sent_for_review !== undefined) {
    params.append('sent_for_review', sent_for_review ? 'true' : 'false')
  }
  if (is_reviewed !== undefined) {
    params.append('is_reviewed', is_reviewed ? 'true' : 'false')
  }
  if (is_archived !== undefined) {
    params.append('is_archived', is_archived ? 'true' : 'false')
  }
  if (is_rejected !== undefined) {
    params.append('is_rejected', is_rejected ? 'true' : 'false')
  }
  if (for_review !== undefined) {
    params.append('for_review', for_review ? 'true' : 'false')
  }
  if (in_progress !== undefined) {
    params.append('in_progress', in_progress ? 'true' : 'false')
  }
  if (q) params.append('q', q)
  if (order_by) params.append('order_by', order_by)
  if (source) params.append('source', source)
  // if() params.append('reviewed_by_id', 'true')

  const data = await request.get<DriverApplicationList>(`/driver-applications/?${params.toString()}`)
  return data
}

export const getDriverApplicationById = async (id: number): Promise<AxiosResponse<DriverApplication>> => {
  const data = await request.get<DriverApplication>(`/driver-applications/${id}`)
  return data
}

export const getDriverApplicationCarInfoById = async (id: number): Promise<AxiosResponse<DriverApplicationCarInfo>> => {
  const data = await request.get<DriverApplicationCarInfo>(`/driver-applications/${id}/car-info/`)
  return data
}

export const getDriverApplicationCarPhotosById = async (
  id: number,
): Promise<AxiosResponse<DriverApplicationCarPhotoItem[]>> => {
  const data = await request.get<DriverApplicationCarPhotoItem[]>(`/driver-applications/${id}/car-photos/`)
  return data
}

export const getDriverApplicationDocumentsById = async (
  id: number,
): Promise<AxiosResponse<DriverApplicationDocumentItem[]>> => {
  const data = await request.get<DriverApplicationDocumentItem[]>(`/driver-applications/${id}/documents/`)
  return data
}

export const getDriverApplicationCheckDocumentsById = async (
  id: number,
): Promise<AxiosResponse<DriverApplicationDocumentItem[]>> => {
  const data = await request.get<DriverApplicationDocumentItem[]>(`/driver-application-check/${id}/documents/`)
  return data
}

export const approveOrRejectDriverApplication = async (
  id: number,
  form: DriverApplication,
): Promise<AxiosResponse<DriverApplication>> => {
  const data = await request.patch<DriverApplication>(`/driver-applications/${id}/`, form)
  return data
}

export const deleteDriverApplicationAdmin = async (id: number): Promise<AxiosResponse<void>> => {
  return request.delete<void>(`/driver-applications/${id}/`)
}

export const sendDriverApplicationReminder = async (id: number): Promise<AxiosResponse<{ message: string }>> => {
  return request.post<{ message: string }>(`/driver-applications/${id}/send-reminder/`)
}

export const getDriverApplicationAnalyticsOverview = async (
  params: {
    date_from?: string
    date_to?: string
  } = {},
): Promise<AxiosResponse<DriverApplicationAnalyticsOverview>> => {
  const search = new URLSearchParams()
  if (params.date_from) search.append('date_from', params.date_from)
  if (params.date_to) search.append('date_to', params.date_to)
  const qs = search.toString()
  return request.get<DriverApplicationAnalyticsOverview>(`/driver-applications/analytics/overview${qs ? `?${qs}` : ''}`)
}
